/**
 * Word-pasted message-thread HTML → plain text (MYP-39).
 *
 * `get_packages_msg` content is warehouse-staff prose typed or pasted into a rich-text box, and it
 * arrives as literal HTML: entities (`&sup2;`, `&nbsp;`), Word's own export cruft (`<o:p>`,
 * `mso-*` inline styles, `<!--[if ...]>` conditional comments, `<style>` blocks), and occasional
 * Wingdings glyphs that only mean anything rendered in that font. `PackageMessagesSheet` has
 * always rendered `content` through plain JSX text, so none of this has ever executed as HTML —
 * but it has always shown the raw tags and entities to the customer as garbled text. That is what
 * this strips.
 *
 * This does not render HTML, and it never will — that is deliberate. `content` is staff-authored,
 * and treating it as HTML (via `dangerouslySetInnerHTML`) is the actual XSS opening the ticket
 * warns about: a compromised or careless staff account could plant a script tag or an event
 * handler that then runs in every customer's browser. Reducing the field to plain text sidesteps
 * that boundary entirely rather than trying to sanitise past it with an allowlist, which is why
 * this can be an ordinary string function instead of a reviewed HTML-sanitiser configuration —
 * there is no HTML left by the time it reaches the page.
 *
 * Kept out of `normalisePackageMessages` on purpose (see that function's doc comment in
 * `packages-wire.ts`) — this is the one place it is applied, at render, in
 * `PackageMessagesSheet`'s `MessageBubble`.
 */

/**
 * Word's own named entities beyond the five basic HTML ones — captured from what actually shows
 * up in pasted content (`&sup2;` is the example the ticket names) plus the usual punctuation Word
 * substitutes for typed characters (smart quotes, en/em dashes, ellipsis). An entity not in this
 * table is left exactly as written rather than guessed at; that is never worse than today's
 * behaviour, which shows every entity literally.
 */
const NAMED_ENTITIES: Readonly<Record<string, string>> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  sup1: '¹',
  sup2: '²',
  sup3: '³',
  deg: '°',
  middot: '·',
  hellip: '…',
  ndash: '–',
  mdash: '—',
  lsquo: '‘',
  rsquo: '’',
  ldquo: '“',
  rdquo: '”',
  trade: '™',
  copy: '©',
  reg: '®',
  times: '×',
  divide: '÷',
  frac12: '½',
  frac14: '¼',
  frac34: '¾',
  bull: '•',
  sect: '§',
  para: '¶',
  euro: '€',
  pound: '£',
  yen: '¥',
  cent: '¢',
  laquo: '«',
  raquo: '»',
  uml: '¨',
  acute: '´',
  cedil: '¸',
  plusmn: '±',
  micro: 'µ',
  ordm: 'º',
  ordf: 'ª',
};

/** Wingdings and similar symbol fonts export into this range — see the note near the bottom. */
function isPrivateUseCodePoint(codePoint: number): boolean {
  return codePoint >= 0xe000 && codePoint <= 0xf8ff;
}

function decodeNumericEntity(body: string): string {
  const isHex = body[1] === 'x' || body[1] === 'X';
  const codePoint = isHex ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
  if (!Number.isFinite(codePoint) || codePoint < 0 || codePoint > 0x10ffff) return '';
  // A numeric entity landing in the Private Use Area is the same Wingdings problem as a literal
  // PUA character below — there is no font-independent glyph to decode it to.
  if (isPrivateUseCodePoint(codePoint)) return '';
  try {
    return String.fromCodePoint(codePoint);
  } catch {
    return '';
  }
}

export function sanitiseMessageContent(raw: string): string {
  let text = raw;

  // Word wraps pasted content in conditional comments (`<!--[if gte mso 9]>...<![endif]-->`) and
  // `<!--StartFragment-->`/`<!--EndFragment-->` markers — ordinary HTML comments syntactically, so
  // one pass removes both. `<style>`/`<script>` carry their own text content, which must go with
  // the tags or their CSS/JS leaks through as prose.
  text = text.replace(/<!--[\s\S]*?-->/g, '');
  text = text.replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, '');

  // Tags that mark a boundary become whitespace before the generic strip below removes them, so
  // paragraphs, list items and table cells do not collide into one run-on line.
  text = text.replace(/<\s*br\b[^>]*>/gi, '\n');
  text = text.replace(/<\s*\/\s*(p|div|li|tr|h[1-6])\s*>/gi, '\n');
  text = text.replace(/<\s*\/\s*(td|th)\s*>/gi, ' ');

  // Everything else that looks like a tag — including Word's namespaced `<o:p>` — is discarded.
  text = text.replace(/<[^>]*>/g, '');

  // Named and numeric entities in one pass over the original text, so a literal "&amp;lt;" is not
  // decoded twice into "<". An unrecognised named entity is left untouched.
  text = text.replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z][a-z0-9]*);/gi, (match, body: string) =>
    body[0] === '#' ? decodeNumericEntity(body) : (NAMED_ENTITIES[body.toLowerCase()] ?? match),
  );

  // A literal Wingdings/symbol-font character pasted straight into the row (not via an entity) —
  // it renders as the intended glyph only in that font, and as a mystery box in any other, so
  // there is no faithful plain-text fallback and it is dropped rather than shown as tofu.
  text = text.replace(/[\u{e000}-\u{f8ff}]/gu, '');

  // Collapse what the removals above leave behind: runs of spaces/tabs to one space, whitespace
  // hugging a newline to just the newline, three or more blank lines to one, then trim the ends.
  text = text.replace(/[ \t]+/g, ' ');
  text = text.replace(/ *\n */g, '\n');
  text = text.replace(/\n{3,}/g, '\n\n');

  return text.trim();
}
